import {
  paymentsControllerFindOne,
  paymentsControllerInitiate,
  requestsControllerFindOne,
} from '@kalba/api-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { AppLayout } from '../../../components/layout/app-layout';
import { Button } from '../../../components/ui/button';
import { Price } from '../../../components/ui/price';
import { useAuthStore } from '../../../stores/auth.store';

export const Route = createFileRoute('/permits/$permitId/payment')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('permit:view')) throw redirect({ to: '/dashboard' });
  },
  component: PaymentPage,
});

interface FeeItem {
  feeName: string;
  payee: string;
  amount: string;
}
interface FeeBreakdown {
  breakdown?: FeeItem[];
}

function PaymentPage() {
  const { permitId } = Route.useParams();
  const navigate = useNavigate();
  const { permissions } = useAuthStore.getState();
  const canPay = permissions.includes('permit:pay' as never);

  const { data: permit, isLoading: permitLoading } = useQuery({
    queryKey: ['permit', permitId],
    queryFn: () =>
      requestsControllerFindOne({ id: permitId }) as unknown as Promise<{
        id: string;
        referenceNo: string;
        status: string;
        totalFee: string;
        feeBreakdown: FeeBreakdown | null;
        pledgeStudy?: { totalFee?: string | null } | null;
      }>,
  });

  const { data: payment, isLoading: paymentLoading } = useQuery({
    queryKey: ['payment', permitId],
    queryFn: () =>
      paymentsControllerFindOne({ permitId }) as unknown as Promise<{
        id: string;
        status: string;
        amount: string;
      } | null>,
    retry: false,
  });

  const initiateMutation = useMutation({
    mutationFn: () => paymentsControllerInitiate({ permitId }) as Promise<{ gatewayUrl: string }>,
    onSuccess: (data) => {
      if (data.gatewayUrl) window.location.href = data.gatewayUrl;
    },
  });

  useEffect(() => {
    if (!canPay) navigate({ to: '/permits/$permitId', params: { permitId } });
  }, [canPay, navigate, permitId]);

  if (!canPay) return null;

  if (permitLoading || paymentLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center p-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </AppLayout>
    );
  }

  if (!permit) return null;

  const feeItems = (permit.feeBreakdown as FeeBreakdown | null)?.breakdown ?? [];
  const pledgeStudyFee = Number(permit.pledgeStudy?.totalFee ?? 0);
  const combinedTotal = Number(permit.totalFee ?? 0) + pledgeStudyFee;

  return (
    <AppLayout>
      <div className="mx-auto max-w-2xl px-6 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Payment</h1>
            <p className="text-sm text-gray-500 mt-0.5">Permit {permit.referenceNo}</p>
          </div>
          <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 uppercase tracking-wide">
            {permit.status.replace(/_/g, ' ')}
          </span>
        </div>

        {/* Fee summary */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Fee Summary</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {feeItems.map((fee) => (
                <tr key={fee.feeName}>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{fee.feeName}</div>
                    <div className="text-xs text-gray-400">Payee: {fee.payee}</div>
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-gray-900">
                    <Price value={Number(fee.amount)} />
                  </td>
                </tr>
              ))}
              {feeItems.length === 0 && (
                <tr>
                  <td colSpan={2} className="px-6 py-8 text-center text-gray-400">
                    No fee breakdown available.
                  </td>
                </tr>
              )}
              {pledgeStudyFee > 0 && (
                <tr>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">Pledge & Study</div>
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-gray-900">
                    <Price value={pledgeStudyFee} />
                  </td>
                </tr>
              )}
            </tbody>
            {combinedTotal > 0 && (
              <tfoot>
                <tr className="bg-gray-50 border-t-2 border-gray-200">
                  <td className="px-6 py-4 font-bold text-gray-900">Total</td>
                  <td className="px-6 py-4 text-right text-xl font-bold text-gray-900">
                    <Price value={combinedTotal} />
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Payment status / action */}
        {payment?.status === 'COMPLETED' ? (
          <div className="flex items-center gap-3 rounded-2xl bg-emerald-50 border border-emerald-200 p-5">
            <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
            <p className="text-sm font-medium text-emerald-800">
              Payment received. Your permit is being processed.
            </p>
          </div>
        ) : payment?.status === 'FAILED' ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 rounded-2xl bg-red-50 border border-red-200 p-5">
              <AlertCircle className="h-6 w-6 text-red-500 shrink-0" />
              <p className="text-sm font-medium text-red-700">
                Your previous payment attempt failed. Please try again.
              </p>
            </div>
            <PayNowButton
              onClick={() => initiateMutation.mutate()}
              isPending={initiateMutation.isPending}
            />
          </div>
        ) : (
          <PayNowButton
            onClick={() => initiateMutation.mutate()}
            isPending={initiateMutation.isPending}
          />
        )}

        {initiateMutation.isError && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">
            Failed to start payment. Please try again.
          </p>
        )}
      </div>
    </AppLayout>
  );
}

function PayNowButton({ onClick, isPending }: { onClick: () => void; isPending: boolean }) {
  return (
    <Button
      variant="primary"
      className="w-full py-3 text-base"
      onClick={onClick}
      disabled={isPending}
    >
      {isPending ? (
        <span className="flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Redirecting to payment…
        </span>
      ) : (
        'Pay Now'
      )}
    </Button>
  );
}
